/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, action, computed, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Header, Segment, Accordion, Icon, Label, Table, Button, Modal, List, Message, Popup } from 'semantic-ui-react';
import TimeAgo from 'react-timeago';
import c from 'classnames';
import { createLink } from '@amzn/base-ui/dist/helpers/routing';
import { displayWarning } from '@amzn/base-ui/dist/helpers/notification';
import { awsRegion } from '@amzn/base-ui/dist/helpers/settings';
import { isAppStreamEnabled } from '../../helpers/settings';
import { getDeployedRegions, onboardAccountRegion, getAccountOnboardCfnTemplate } from '../../helpers/api';
import { regionDisplayNames, getRegionDisplayText } from '../../models/constants/aws-regions';
import CopyToClipboard from '../helpers/CopyToClipboard';

/**
 * Renders a region code as a JSX fragment with the friendly name bolded.
 * e.g. <b>Sydney</b> (ap-southeast-2)
 * Falls back to the raw code if no friendly name is known.
 */
function renderRegionName(regionCode) {
  const friendly = regionDisplayNames[regionCode];
  if (!friendly) return regionCode;
  return (
    <span>
      <b>{friendly}</b> ({regionCode})
    </span>
  );
}

const { getAccountIdsOfActiveEnvironments } = require('./AccountUtils');

const statusDisplay = {
  CURRENT: { color: 'green', display: 'Up-to-Date', spinner: false },
  NEEDS_UPDATE: { color: 'orange', display: 'Needs Update', spinner: false },
  NEEDS_ONBOARD: { color: 'purple', display: 'Needs Onboarding', spinner: false },
  ERRORED: { color: 'red', display: 'Error', spinner: false },
  PENDING: { color: 'yellow', display: 'Pending', spinner: true },
  UNKNOWN: { color: 'grey', display: 'Unknown', spinner: false },
};

// Priority order for worst-case status (lower index = worse)
const statusPriority = ['ERRORED', 'NEEDS_ONBOARD', 'NEEDS_UPDATE', 'PENDING', 'UNKNOWN', 'CURRENT'];

function getWorstStatus(statuses) {
  for (const s of statusPriority) {
    if (statuses.includes(s)) return s;
  }
  return 'UNKNOWN';
}

class AccountCard extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.expandedRegions = {}; // { 'ap-southeast-2': true, 'us-west-2': false }
      this.isSelected = false;
      this.permButtonLoading = false;
      this.addRegionModalOpen = false;
      this.deployedRegions = { primaryRegion: '', additionalRegions: [] };
      this.onboardingRegion = '';
      this.onboardingLoading = false;
      this.onboardingResult = null;
      this.onboardingError = '';
      this.retrieveTemplateRegion = '';
      this.retrieveTemplateLoading = false;
      this.retrieveTemplateResult = null;
      this.retrieveTemplateError = '';
      this.reOnboardModalOpen = false;
    });
  }

  get account() {
    return this.props.account;
  }
  get awsAccountsStore() {
    return this.props.awsAccountsStore;
  }

  get isSelectable() {
    return this.props.isSelectable;
  }

  get appStreamStatusMismatch() {
    return isAppStreamEnabled && !this.account.isAppStreamConfigured;
  }

  get permissionStatus() {
    return this.account.permissionStatus;
  }

  get primaryRegion() {
    return awsRegion || '';
  }

  get regionConfigs() {
    return this.account.regionConfigs || {};
  }

  // Compute worst-case status across primary + all additional regions
  get overallStatus() {
    const statuses = [this.permissionStatus];
    _.forEach(this.regionConfigs, (config) => {
      if (config.status) statuses.push(config.status);
    });
    return getWorstStatus(statuses);
  }

  goto(pathname) {
    const location = this.props.location;
    const link = createLink({ location, pathname });
    this.props.history.push(link);
  }

  handleToggleRegion = (region) => {
    this.expandedRegions = { ...this.expandedRegions, [region]: !this.expandedRegions[region] };
  };

  handleSelected = () => {
    this.isSelected = !this.isSelected;
  };

  handleUpdatePermission() {
    const awsAccountUUID = this.account.id;
    if (this.appStreamStatusMismatch) {
      this.goto(`/aws-accounts/update/${awsAccountUUID}/rev/${this.account.rev}`);
    } else {
      this.goto(`/aws-accounts/onboard/${awsAccountUUID}`);
    }
  }

  handleOpenAddRegionModal = async () => {
    runInAction(() => {
      this.addRegionModalOpen = true;
      this.onboardingResult = null;
      this.onboardingError = '';
      this.onboardingRegion = '';
    });
    try {
      const result = await getDeployedRegions();
      runInAction(() => {
        this.deployedRegions = result;
      });
    } catch (e) {
      runInAction(() => {
        this.onboardingError = e.message || 'Failed to load deployed regions';
      });
    }
  };

  handleCloseAddRegionModal = () => {
    this.addRegionModalOpen = false;
    this.onboardingResult = null;
    this.onboardingError = '';
    // Refresh the accounts store to pick up any regionConfigs changes
    this.awsAccountsStore.load();
  };

  handleOnboardRegion = async (region) => {
    runInAction(() => {
      this.onboardingRegion = region;
      this.onboardingLoading = true;
      this.onboardingResult = null;
      this.onboardingError = '';
    });
    try {
      const result = await onboardAccountRegion(this.account.id, region);
      runInAction(() => {
        this.onboardingResult = result;
        this.onboardingLoading = false;
      });
    } catch (e) {
      runInAction(() => {
        this.onboardingError = e.message || 'Failed to onboard region';
        this.onboardingLoading = false;
      });
    }
  };

  handleRetrieveTemplate = async (region) => {
    runInAction(() => {
      this.retrieveTemplateRegion = region;
      this.retrieveTemplateLoading = true;
      this.retrieveTemplateResult = null;
      this.retrieveTemplateError = '';
    });
    try {
      const isPrimary = region === this.primaryRegion;
      const result = isPrimary
        ? await getAccountOnboardCfnTemplate(this.account.id)
        : await onboardAccountRegion(this.account.id, region);
      // Normalize: primary API doesn't include region in response
      if (!result.region) result.region = region;
      runInAction(() => {
        this.retrieveTemplateResult = result;
        this.retrieveTemplateLoading = false;
        this.reOnboardModalOpen = true;
      });
    } catch (e) {
      runInAction(() => {
        this.retrieveTemplateError = e.message || 'Failed to retrieve onboarding template';
        this.retrieveTemplateLoading = false;
      });
    }
  };

  handleDismissRetrieveTemplate = () => {
    this.reOnboardModalOpen = false;
    this.retrieveTemplateResult = null;
    this.retrieveTemplateError = '';
    this.retrieveTemplateRegion = '';
    // Refresh accounts to pick up any status changes from re-onboard
    this.awsAccountsStore.load();
  };

  render() {
    const account = this.account;
    const attrs = {};
    if (this.isSelected) attrs.color = 'blue';
    const overallStatus = this.overallStatus;

    return (
      <Segment clearing padded raised className="mb3" {...attrs}>
        <div className="flex">
          <div className="flex-auto mb1">
            {this.renderStatusLabel(overallStatus, 'top left')}
            {this.renderButtons()}
            {this.renderHeader(account)}
            {this.renderDescription(account)}
            {this.permissionStatus === 'NEEDS_ONBOARD' && this.renderUpdatePermsButton()}
            {this.renderPrimaryRegionAccordion(account)}
            {this.renderAdditionalRegionAccordions()}
          </div>
        </div>
        {this.renderAddRegionModal()}
      </Segment>
    );
  }

  renderStatusLabel(status, attached) {
    const state = statusDisplay[status] || statusDisplay.UNKNOWN;
    const props = attached ? { attached, size: 'mini' } : { size: 'mini' };
    return (
      <Label color={state.color} {...props}>
        {state.spinner && <Icon name="spinner" loading />}
        {state.display}
      </Label>
    );
  }

  renderHeader(account) {
    const isSelectable = this.isSelectable;
    const onClickAttr = {};
    const idReadable = account.accountId.replace(/(.{4})(.{4})/g, '$1-$2-');
    if (isSelectable) onClickAttr.onClick = () => this.handleSelected();
    return (
      <div>
        <Header as="h3" color="blue" className={c('mt2', isSelectable ? 'cursor-pointer' : '')} {...onClickAttr}>
          {account.name}
          <Header.Subheader>
            <span className="pt1 fs-8 color-grey">AWS Account #{idReadable}</span>
          </Header.Subheader>
        </Header>
      </div>
    );
  }

  renderDescription(account) {
    return <div>{account.description}</div>;
  }

  renderButtons() {
    const isOnboarded = this.permissionStatus !== 'NEEDS_ONBOARD' && this.permissionStatus !== 'PENDING';
    return (
      <>
        {isOnboarded && (
          <Button floated="right" basic color="blue" onClick={this.handleOpenAddRegionModal}>
            Add Region
          </Button>
        )}
      </>
    );
  }

  renderPrimaryRegionAccordion(account) {
    const primaryRegion = this.primaryRegion;
    const status = this.permissionStatus;
    const isExpanded = !!this.expandedRegions[primaryRegion];
    const isOnboarded = status !== 'NEEDS_ONBOARD';
    const isPending = status === 'PENDING';
    const errored = status === 'ERRORED';

    const rowKeyVal = {
      roleArn: 'Role ARN',
      externalId: 'External ID',
      vpcId: 'VPC ID',
      subnetId: 'Subnet ID',
      encryptionKeyArn: 'Encryption Key ARN',
      cfnStackId: 'CloudFormation Stack ID',
    };

    return (
      <Accordion className="mt2">
        <Accordion.Title active={isExpanded} index={0} onClick={() => this.handleToggleRegion(primaryRegion)}>
          <Icon name="dropdown" />
          Details - {renderRegionName(primaryRegion)}{' '}
          <Label size="mini" color="blue">
            Primary
          </Label>{' '}
          {this.renderStatusLabel(status)}
        </Accordion.Title>
        <Accordion.Content active={isExpanded}>
          {isExpanded && !isOnboarded && (
            <div className="mb2">
              This account needs to be onboarded. Click &apos;Onboard Account&apos; to finish setting up.
            </div>
          )}
          {isExpanded && isPending && (
            <div className="mb2">
              Service Workbench is waiting for the CFN stack to complete. Please wait a few minutes.
            </div>
          )}
          {isExpanded && errored && (
            <div className="mb2">
              Something went wrong while checking the CFN stack. Please check the AWS Management Console.
            </div>
          )}
          {isExpanded && isOnboarded && this.renderRetrieveTemplateButton(primaryRegion)}
          {isExpanded && isOnboarded && (
            <Table striped>
              <Table.Body>
                {Object.keys(rowKeyVal).map((entry) => (
                  <Table.Row key={entry}>
                    <Table.Cell>{rowKeyVal[entry]}</Table.Cell>
                    <Table.Cell>{account[entry]}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Accordion.Content>
      </Accordion>
    );
  }

  renderAdditionalRegionAccordions() {
    const regionConfigs = this.regionConfigs;
    const regions = Object.keys(regionConfigs);
    if (regions.length === 0) return null;

    const regionRowKeyVal = {
      vpcId: 'VPC ID',
      subnetId: 'Subnet ID',
      encryptionKeyArn: 'Encryption Key ARN',
      cfnStackId: 'CloudFormation Stack ID',
    };

    return regions.map((region) => {
      const config = regionConfigs[region];
      const status = config.status || 'UNKNOWN';
      const isExpanded = !!this.expandedRegions[region];

      return (
        <Accordion key={region} className="mt1">
          <Accordion.Title active={isExpanded} index={0} onClick={() => this.handleToggleRegion(region)}>
            <Icon name="dropdown" />
            Details - {renderRegionName(region)} {this.renderStatusLabel(status)}
          </Accordion.Title>
          <Accordion.Content active={isExpanded}>
            {isExpanded && status === 'PENDING' && (
              <div className="mb2">Waiting for the regional CFN stack to complete in {renderRegionName(region)}.</div>
            )}
            {isExpanded && status === 'ERRORED' && (
              <div className="mb2">
                Something went wrong with the regional stack in {renderRegionName(region)}. Check the AWS Management
                Console.
              </div>
            )}
            {isExpanded &&
              (status === 'CURRENT' || status === 'NEEDS_UPDATE' || status === 'PENDING') &&
              this.renderRetrieveTemplateButton(region)}
            {isExpanded && config.vpcId && (
              <Table striped>
                <Table.Body>
                  {Object.keys(regionRowKeyVal).map((entry) =>
                    config[entry] ? (
                      <Table.Row key={entry}>
                        <Table.Cell>{regionRowKeyVal[entry]}</Table.Cell>
                        <Table.Cell>{config[entry]}</Table.Cell>
                      </Table.Row>
                    ) : null,
                  )}
                </Table.Body>
              </Table>
            )}
          </Accordion.Content>
        </Accordion>
      );
    });
  }

  renderRetrieveTemplateButton(region) {
    const isLoading = this.retrieveTemplateLoading && this.retrieveTemplateRegion === region;
    const hasError = this.retrieveTemplateError && this.retrieveTemplateRegion === region;
    const isPrimary = region === this.primaryRegion;
    const buttonLabel = isPrimary ? 'Update Primary Region' : <>Update {renderRegionName(region)}</>;

    return (
      <div className="mb2">
        {hasError && <Message negative>{this.retrieveTemplateError}</Message>}
        <Button
          basic
          color="blue"
          size="small"
          loading={isLoading}
          disabled={this.retrieveTemplateLoading}
          onClick={() => this.handleRetrieveTemplate(region)}
        >
          {buttonLabel}
        </Button>
        <Popup
          trigger={<Icon name="info circle" color="grey" style={{ marginLeft: '4px', cursor: 'pointer' }} />}
          content="Retrieves the CloudFormation template and update link for this region. Triggers a status recheck — if the deployed template is current, the region will return to a healthy state."
          position="right center"
          size="small"
        />
        {this.retrieveTemplateRegion === region && this.renderReOnboardModal()}
      </div>
    );
  }

  renderReOnboardModal() {
    return (
      <Modal open={this.reOnboardModalOpen} onClose={this.handleDismissRetrieveTemplate} size="small">
        <Modal.Header>
          Re-Onboard - {this.account.name}
          <Icon
            name="close"
            style={{ float: 'right', cursor: 'pointer' }}
            onClick={this.handleDismissRetrieveTemplate}
          />
        </Modal.Header>
        <Modal.Content>{this.retrieveTemplateResult && this.renderOnboardingResult({ useUpdate: true })}</Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleDismissRetrieveTemplate}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  }

  renderAddRegionModal() {
    const { primaryRegion, additionalRegions } = this.deployedRegions;
    const onboardedRegions = Object.keys(this.regionConfigs);
    const allDeployedRegions = [primaryRegion, ...additionalRegions].filter(Boolean);
    const notOnboarded = additionalRegions.filter((r) => !onboardedRegions.includes(r));
    const allOnboarded = notOnboarded.length === 0 && additionalRegions.length > 0;

    return (
      <Modal open={this.addRegionModalOpen} onClose={this.handleCloseAddRegionModal} size="small">
        <Modal.Header>
          Add Region - {this.account.name}
          <Icon name="close" style={{ float: 'right', cursor: 'pointer' }} onClick={this.handleCloseAddRegionModal} />
        </Modal.Header>
        <Modal.Content>
          {this.onboardingError && <Message negative>{this.onboardingError}</Message>}

          {this.onboardingResult && this.renderOnboardingResult()}

          {!this.onboardingResult && (
            <>
              {allDeployedRegions.length === 0 && <Message info>Loading deployed regions...</Message>}

              {allDeployedRegions.length > 0 && (
                <List divided relaxed>
                  {/* Primary region */}
                  <List.Item key={primaryRegion}>
                    <List.Content floated="right">
                      <Button size="small" color="green" basic disabled>
                        Onboarded
                      </Button>
                    </List.Content>
                    <List.Content>
                      <List.Header>
                        {renderRegionName(primaryRegion)}{' '}
                        <Label size="mini" color="blue">
                          Primary
                        </Label>
                      </List.Header>
                    </List.Content>
                  </List.Item>

                  {/* Additional regions */}
                  {additionalRegions.map((region) => {
                    const isOnboarded = onboardedRegions.includes(region);
                    return (
                      <List.Item key={region}>
                        <List.Content floated="right">
                          {isOnboarded ? (
                            <Button size="small" color="green" basic disabled>
                              Onboarded
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="blue"
                              onClick={() => this.handleOnboardRegion(region)}
                              loading={this.onboardingLoading && this.onboardingRegion === region}
                              disabled={this.onboardingLoading}
                            >
                              Onboard
                            </Button>
                          )}
                        </List.Content>
                        <List.Content>
                          <List.Header>{renderRegionName(region)}</List.Header>
                        </List.Content>
                      </List.Item>
                    );
                  })}
                </List>
              )}

              {allOnboarded && (
                <Message info className="mt2">
                  All available regions are onboarded for this account.
                </Message>
              )}

              {additionalRegions.length === 0 && primaryRegion && (
                <Message info className="mt2">
                  No additional regions have been deployed. Configure <code>additionalRegions</code> in your stage
                  settings and redeploy to enable multi-region.
                </Message>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleCloseAddRegionModal}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  }

  renderOnboardingResult({ useUpdate = false } = {}) {
    const result = this.onboardingResult || this.retrieveTemplateResult;
    const { createStackUrl, updateStackUrl, region, urlExpiry, name, template } = result;
    const stackUrl = useUpdate ? updateStackUrl : createStackUrl;
    const stackAction = useUpdate ? 'Update Stack' : 'Create Stack';
    const expired = urlExpiry && Date.now() > urlExpiry;

    return (
      <Segment color="blue">
        <Header as="h4">
          <Icon name="cloud" />
          Deploy Stack in {renderRegionName(region)}
        </Header>

        <Header as="h5" className="mb2 mt3">
          CloudFormation Stack Name
        </Header>
        <div className="mb2 flex">
          <div className="flex-auto">
            <input readOnly value={name || ''} style={{ width: '100%', padding: '8px', fontFamily: 'monospace' }} />
          </div>
          <div className="mt0 p0 ml1">
            <CopyToClipboard text={name || ''} />
          </div>
        </div>
        {template && (
          <div className="mb3 flex">
            <div className="flex-auto">
              <textarea
                readOnly
                value={template}
                rows={10}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>
            <div className="mt0 p0 ml1">
              <CopyToClipboard text={template} />
            </div>
          </div>
        )}

        <List ordered size="medium">
          <List.Item>
            In a separate browser tab, log in to the AWS console for this account.
            <Message className="mr3 mt2 mb2">
              <p>Ensure you are logged in to the correct AWS account in region {renderRegionName(region)}.</p>
            </Message>
          </List.Item>
          <List.Item>
            Click the button below to open the CloudFormation console and {useUpdate ? 'update' : 'create'} the regional
            stack.
            <div className="mb0 flex mt2">
              <div className="flex-auto">
                <Button fluid as="a" target="_blank" href={stackUrl} rel="noopener noreferrer" color="blue">
                  {stackAction}
                </Button>
                {expired ? (
                  <div className="fs-9 center color-red mt1">
                    Expired <TimeAgo date={urlExpiry} />
                  </div>
                ) : (
                  <div className="fs-9 center mt1">
                    Expires <TimeAgo date={urlExpiry} />
                  </div>
                )}
              </div>
              <div className="mt1 p0">
                <CopyToClipboard text={stackUrl} />
              </div>
            </div>
          </List.Item>
          <List.Item>
            After {useUpdate ? 'updating' : 'creating'} the stack, SWB will automatically detect it and onboard the
            region. Close this dialog and wait for the status to update.
          </List.Item>
        </List>
      </Segment>
    );
  }

  async checkForActiveAccounts() {
    runInAction(() => {
      this.permButtonLoading = true;
    });
    const scEnvironmentStore = this.props.scEnvironmentsStore;
    const indexesStore = this.props.indexesStore;
    const projectsStore = this.props.projectsStore;

    await Promise.all([scEnvironmentStore.doLoad(), indexesStore.doLoad(), projectsStore.doLoad()]);
    const scEnvs = scEnvironmentStore.list;
    const indexes = indexesStore.list;
    const projects = projectsStore.list;

    const accountHasActiveEnv = getAccountIdsOfActiveEnvironments(scEnvs, projects, indexes).includes(
      this.props.account.id,
    );
    runInAction(() => {
      this.permButtonLoading = false;
    });

    if (accountHasActiveEnv) {
      displayWarning('Please terminate all workspaces in this account before upgrading the account');
    } else {
      this.handleUpdatePermission();
    }
  }

  renderUpdatePermsButton() {
    return (
      <Button
        floated="right"
        color="purple"
        onClick={() => this.handleUpdatePermission()}
        loading={this.permButtonLoading}
      >
        Onboard Account
      </Button>
    );
  }
}

decorate(AccountCard, {
  handleToggleRegion: action,
  handleSelected: action,
  handleOpenAddRegionModal: action,
  handleCloseAddRegionModal: action,
  handleOnboardRegion: action,
  handleRetrieveTemplate: action,
  handleDismissRetrieveTemplate: action,
  account: computed,
  expandedRegions: observable,
  isSelectable: computed,
  isSelected: observable,
  permissionStatus: computed,
  overallStatus: computed,
  primaryRegion: computed,
  regionConfigs: computed,
  permButtonLoading: observable,
  addRegionModalOpen: observable,
  deployedRegions: observable,
  onboardingRegion: observable,
  onboardingLoading: observable,
  onboardingResult: observable,
  onboardingError: observable,
  retrieveTemplateRegion: observable,
  retrieveTemplateLoading: observable,
  retrieveTemplateResult: observable,
  retrieveTemplateError: observable,
  reOnboardModalOpen: observable,
});

export default inject(
  'awsAccountsStore',
  'scEnvironmentsStore',
  'indexesStore',
  'projectsStore',
)(withRouter(observer(AccountCard)));
